import { convertDate, getDateInLocaleString } from '~/helpers/date';
import type {
	IDriverRequests,
	ICurrentCargoFullInfo,
	IGetHeaderInfo,
	IGetHeaderInfoResponse,
	IReqSavePickupDetails
} from '~~/shared/interfaces/driverRequests';

export const useDriverRequestStore = defineStore('driver-requests', () => {
	const driverHeaderInfo = ref<IGetHeaderInfo | null>(null);
	const allDriverRequests = ref<IDriverRequests | null>(null);
	const filterDriverRequestsType = ref<null | number>(null);
	const chooseDate = ref<string | undefined>(convertDate(new Date()));
	const currentCargo = ref<ICurrentCargoFullInfo | null>(null);
	const abortControllers: Record<string, AbortController> = {};

	const abortRequest = (key: string) => {
		if (abortControllers[key]) {
			abortControllers[key].abort();
		}
		abortControllers[key] = new AbortController();
		return abortControllers[key].signal;
	};

	const loading = reactive({
		allDriverRequests: false,
		filterDriverRequestsType: null,
		getCargoById: false,
		savePickupDetails: {}
	});

	function $reset() {
		allDriverRequests.value = null;
		filterDriverRequestsType.value = null;
		driverHeaderInfo.value = null;
	}

	const setFilterDriverRequestsType = (type: null | number) => {
		filterDriverRequestsType.value = type;
	};

	const getHeaderInfo = async () => {
		const signal = abortRequest('getHeaderInfo');
		const { data, status } = await useCustomFetch<IGetHeaderInfoResponse>('/api/DriverRequests/get-header-info', {
			query: { date: chooseDate.value },
			signal
		});

		if (status.value === 'success' && data.value) {
			driverHeaderInfo.value = data.value;
		}
	};

	const savePickupDetails = async (payload: IReqSavePickupDetails) => {
		const { data, status } = await useCustomFetch<any>(
			'/api/DriverRequests/save-pickup-details',
			{
				method: 'POST',
				query: payload.query,
				body: payload.body
			},
			{
				setLoading: (load: boolean) => {
					if (true) {
						loading.savePickupDetails[payload.query.status] = load;
					}
				}
			}
		);

		if (status.value === 'success' && data.value) {
			getDriverRequests(false);
		}
		return { data: data.value, status: status.value };
	};

	const getDriverRequests = async (isNeedLoader = false) => {
		const signal = abortRequest('driverRequests');

		const { data, status } = await useCustomFetch<IDriverRequests>(
			'/api/DriverRequests/get-all',
			{
				query: { filter: filterDriverRequestsType.value, date: chooseDate.value },
				signal
			},
			{
				setLoading: (load: boolean) => {
					if (isNeedLoader) {
						loading.allDriverRequests = load;
					}
				}
			}
		);

		if (status.value === 'success' && data.value) {
			allDriverRequests.value = data.value;
		}
	};

	const getCargoById = async (payload: { id: string }, isNeedLoader = false, forceRefresh = false) => {
		const { data, status } = await useCustomFetch<ICurrentCargoFullInfo>(
			'/api/DriverRequests/get-cargo-by-id',
			{
				query: payload
			},
			{
				setLoading: (load: boolean) => {
					if (isNeedLoader) {
						loading.getCargoById = load;
					}
				},
				isSilent: forceRefresh
			}
		);

		if (status.value === 'success' && data.value) {
			currentCargo.value = data.value;
		}

		return { data: data.value, status: status.value };
	};

	const resetCurrentCargo = () => {
		currentCargo.value = null;
	};

	const deleteCurrentCargoComment = (payloadCommentId: { id: string }) => {
		if (currentCargo.value) {
			currentCargo.value.cargoComments = currentCargo.value.cargoComments.filter(
				(comment) => comment.id !== payloadCommentId.id
			);
		}
	};

	const deleteCurrentCargoImage = (payloadImageId: { id: string }) => {
		if (currentCargo.value) {
			currentCargo.value.cargoImages = currentCargo.value.cargoImages.filter((image) => image.id !== payloadImageId.id);
		}
	};

	return {
		driverHeaderInfo,
		allDriverRequests,
		filterDriverRequestsType,
		loading,
		currentCargo,
		chooseDate,
		$reset,
		getHeaderInfo,
		getDriverRequests,
		setFilterDriverRequestsType,
		getCargoById,
		deleteCurrentCargoComment,
		deleteCurrentCargoImage,
		savePickupDetails,
		resetCurrentCargo
	};
});
